import { useEffect, useState } from 'react';
import { ImagePlus, Loader2, Palette } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useBranding } from '@/context/BrandingContext';
import api from '@/lib/api';

const TEXT_FIELDS = [
  ['company_name', 'Nama Perusahaan', 200],
  ['application_name', 'Nama Aplikasi', 80],
  ['tagline', 'Tagline', 160],
  ['footer_text', 'Teks Footer', 200],
  ['copyright_text', 'Copyright', 200],
  ['contact_email', 'Email Perusahaan', 200, 'email'],
  ['contact_phone', 'Nomor Telepon', 50],
  ['website', 'Website', 300, 'url'],
];

const ASSETS = [
  ['sidebar-logo', 'sidebar_logo_url', 'Logo Sidebar', 'Logo yang tampil setelah pengguna masuk.', 2],
  ['login-logo', 'login_logo_url', 'Logo Login', 'Logo pada halaman login.', 2],
  ['favicon', 'favicon_url', 'Favicon', 'Ikon kecil pada tab browser.', 2],
  ['login-background', 'login_background_url', 'Background Login', 'Gambar latar halaman login.', 5],
];

const THEME_PROFILES = {
  modern: { title: 'PCCS Modern', description: 'Aksen indigo modern untuk produk PCCS.', color: '#6366F1' },
  forte: { title: 'Forte Signature', description: 'Palet coklat khas Forte Creative Build.', color: '#8A5A36' },
};

export default function BrandingPage() {
  const { branding, save, refresh } = useBranding();
  const [form, setForm] = useState(branding);
  const [working, setWorking] = useState('');
  useEffect(() => setForm(branding), [branding]);

  const setValue = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const chooseProfile = (profile) => setForm((current) => ({
    ...current,
    theme_profile: profile,
    // A profile is a complete visual choice. Keep the editable color in sync
    // so a stale custom value can no longer override the selected profile.
    primary_color: THEME_PROFILES[profile].color,
  }));
  const submit = async (event) => {
    event.preventDefault();
    setWorking('save');
    try {
      await save({
        company_name: form.company_name, application_name: form.application_name,
        tagline: form.tagline, primary_color: form.primary_color,
        footer_text: form.footer_text, copyright_text: form.copyright_text,
        contact_email: form.contact_email || null, contact_phone: form.contact_phone,
        website: form.website, timezone: form.timezone, currency: form.currency,
        language: form.language, default_theme: form.default_theme, theme_profile: form.theme_profile,
      });
      toast.success('Identitas produk berhasil diperbarui.');
    } catch (error) {
      toast.error(error?.response?.data?.detail?.[0]?.msg || error?.response?.data?.detail || 'Pengaturan gagal disimpan.');
    } finally { setWorking(''); }
  };

  const uploadAsset = async (kind, maxMb, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > maxMb * 1024 * 1024) { toast.error(`Ukuran maksimum ${maxMb} MB.`); return; }
    setWorking(kind);
    try {
      const body = new FormData(); body.append('upload', file);
      await api.post(`/branding/assets/${kind}`, body);
      await refresh();
      toast.success('Aset branding berhasil diperbarui.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Aset gagal diunggah.');
    } finally { setWorking(''); }
  };

  return <div className="animate-in-up max-w-5xl space-y-6">
    <div><h1 className="flex items-center gap-2 text-3xl font-semibold tracking-tight"><Palette className="h-7 w-7 text-[var(--fpccs-primary)]" /> Branding & Identitas</h1><p className="mt-1 text-sm text-muted-fpccs">Kelola seluruh identitas produk tanpa mengubah source code.</p></div>

    <form onSubmit={submit} className="space-y-6">
      <section className="card-fpccs p-5">
        <h2 className="font-semibold">Identitas & Kontak</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {TEXT_FIELDS.map(([key, label, maxLength, type]) => <Field key={key} label={label}><Input type={type || 'text'} required={['company_name', 'application_name'].includes(key)} minLength={['company_name', 'application_name'].includes(key) ? 2 : undefined} maxLength={maxLength} value={form[key] || ''} onChange={(event) => setValue(key, event.target.value)} /></Field>)}
          <Field label="Warna Utama"><div className="flex gap-3"><input aria-label="Warna utama" type="color" value={form.primary_color} onChange={(event) => setValue('primary_color', event.target.value.toUpperCase())} className="h-10 w-14 rounded border border-[var(--fpccs-border)] bg-transparent p-1" /><Input pattern="^#[0-9A-Fa-f]{6}$" required value={form.primary_color} onChange={(event) => setValue('primary_color', event.target.value)} className="max-w-[160px] font-mono" /></div></Field>
        </div>
      </section>

      <section className="card-fpccs p-5">
        <h2 className="font-semibold">Regional</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Field label="Timezone"><Select value={form.timezone} onChange={(value) => setValue('timezone', value)} options={[['Asia/Jakarta', 'Asia/Jakarta (WIB)'], ['Asia/Makassar', 'Asia/Makassar (WITA)'], ['Asia/Jayapura', 'Asia/Jayapura (WIT)'], ['UTC', 'UTC']]} /></Field>
          <Field label="Mata Uang"><Select value={form.currency} onChange={(value) => setValue('currency', value)} options={[['IDR', 'IDR — Rupiah'], ['USD', 'USD — Dollar'], ['SGD', 'SGD — Singapore Dollar'], ['MYR', 'MYR — Ringgit']]} /></Field>
          <Field label="Bahasa"><Select value={form.language} onChange={(value) => setValue('language', value)} options={[['id', 'Bahasa Indonesia'], ['en', 'English']]} /></Field>
          <Field label="Tema Default"><Select value={form.default_theme} onChange={(value) => setValue('default_theme', value)} options={[['system', 'Ikuti perangkat'], ['light', 'Terang'], ['dark', 'Gelap']]} /></Field>
        </div>
      </section>

      <section className="card-fpccs p-5">
        <h2 className="font-semibold">Profil Tampilan</h2>
        <p className="mt-1 text-sm text-muted-fpccs">PCCS Modern adalah default netral untuk client. Forte Signature mempertahankan identitas coklat Forte dan tetap mendukung mode terang, gelap, atau perangkat.</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <ThemeProfileCard active={form.theme_profile !== 'forte'} {...THEME_PROFILES.modern} onClick={() => chooseProfile('modern')} />
          <ThemeProfileCard active={form.theme_profile === 'forte'} {...THEME_PROFILES.forte} onClick={() => chooseProfile('forte')} />
        </div>
      </section>

      <section className="card-fpccs p-5">
        <h2 className="font-semibold">Aset Visual</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          {ASSETS.map(([kind, field, label, description, maxMb]) => <AssetField key={kind} label={label} description={description} src={branding[field] || (kind !== 'login-background' ? branding.logo_url : '')} busy={working === kind} onChange={(event) => uploadAsset(kind, maxMb, event)} maxMb={maxMb} wide={kind === 'login-background'} />)}
        </div>
      </section>

      <section className="card-fpccs p-5">
        <div className="text-xs text-muted-fpccs">Preview</div>
        <div className="mt-3 flex items-center gap-3"><img src={branding.sidebar_logo_url || branding.logo_url || '/fpccs-mark.svg'} alt="Preview" className="h-12 w-12 rounded-lg object-contain bg-black/20" /><div><div className="text-xl font-semibold" style={{ color: form.primary_color }}>{form.application_name}</div><div className="text-sm text-muted-fpccs">{form.company_name}{form.tagline ? ` · ${form.tagline}` : ''}</div></div></div>
        <div className="mt-4 border-t border-[var(--fpccs-border)] pt-3 text-xs text-muted-fpccs">{form.copyright_text || `© ${new Date().getFullYear()} ${form.company_name}`}{form.footer_text ? ` · ${form.footer_text}` : ''}</div>
      </section>

      <Button type="submit" disabled={!!working}>{working === 'save' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Simpan Semua Pengaturan</Button>
    </form>
  </div>;
}

function Field({ label, children }) { return <div className="space-y-1.5"><Label className="text-xs text-muted-fpccs">{label}</Label>{children}</div>; }
function ThemeProfileCard({ active, title, description, color, onClick }) { return <button type="button" onClick={onClick} className={`rounded-xl border p-4 text-left transition ${active ? 'border-[var(--fpccs-primary)] bg-[var(--fpccs-surface-2)] ring-1 ring-[var(--fpccs-primary)]' : 'border-[var(--fpccs-border)] hover:bg-[var(--fpccs-surface-2)]'}`}><span className="mb-3 block h-2 w-full rounded-full" style={{ backgroundColor: color }} /><span className="block text-sm font-semibold">{title}</span><span className="mt-1 block text-xs text-muted-fpccs">{description}</span></button>; }
function Select({ value, onChange, options }) { return <select value={value || ''} onChange={(event) => onChange(event.target.value)} className="flex h-10 w-full rounded-md border border-[var(--fpccs-border)] bg-[var(--fpccs-surface-1)] px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-[var(--fpccs-primary)]">{options.map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select>; }
function AssetField({ label, description, src, busy, onChange, maxMb, wide }) { return <div className="rounded-xl border border-[var(--fpccs-border)] p-4"><div className="flex gap-3">{src ? <img src={src} alt={label} className={`${wide ? 'h-16 w-28' : 'h-14 w-14'} rounded-lg bg-black/20 object-contain`} /> : <div className={`${wide ? 'h-16 w-28' : 'h-14 w-14'} flex items-center justify-center rounded-lg bg-black/20`}><ImagePlus className="h-5 w-5 text-muted-fpccs" /></div>}<div className="min-w-0"><div className="text-sm font-medium">{label}</div><div className="mt-1 text-xs text-muted-fpccs">{description}</div></div></div><Input type="file" accept="image/png,image/jpeg,image/webp" onChange={onChange} disabled={busy} className="mt-3" /><div className="mt-1 text-[10px] text-muted-fpccs">PNG, JPEG, atau WebP · maksimum {maxMb} MB</div></div>; }
